import { Spinner } from '@common/components';
import { useCheckForArrivalDate } from '@common/hooks';
import React, { FC, useState } from 'react';

import { Routes, EMPTY_CONTENT_PAGE } from '@common/constants';

import { EntitiesListing } from '@features/EntitiesListing';

import { hasAvailability } from '@utils/helpers';
import { Property } from 'shared-types/Property';
import { Category, JourneyParams } from 'shared-types/Common';
import { useRouter } from 'shared-routing';
import { SpaEntityCategoryView } from '@common/components/SpaEntity/SpaEntityCategoryView';
import { SpaEntityError } from '@common/components/SpaEntity/SpaEntityError';
import { useGetSpaFacilitiesPage, useGetSpaSettings, useGetSpaFacilities } from '@features/spa';
import { getSpaDateString } from '@features/spa/pages/SpaTreatments/SpaTreatments';

export type FacilitiesProps = {
  categoryId: number | null;
  property: Property;
  isFacilityCategoryViewVisible: boolean;
  facilitiesCategories: Category[];
  journeyParams: JourneyParams;
  changeJourneyParam: () => void;
  userHasAccessToFacilities: boolean;
  language: string;
};

export const Facilities: FC<FacilitiesProps> = ({
  journeyParams,
  property,
  changeJourneyParam,
  facilitiesCategories = [],
  categoryId = null,
  userHasAccessToFacilities = false,
  language,
}) => {
  const [categoryFilterId, setCategoryFilterId] = useState(categoryId);
  const { data: facilities = [], isFetching: isFetchingFacilities } = useGetSpaFacilities(
    property.id,
    language,
    getSpaDateString(journeyParams.spaDate),
    categoryFilterId
  );

  const router = useRouter();
  const { data: facilityPageData } = useGetSpaFacilitiesPage(property.id, language);
  const facilityPage = facilityPageData ?? EMPTY_CONTENT_PAGE;
  const { data: spaSettings } = useGetSpaSettings(property.id);

  const isFacilityCategoryViewVisible = spaSettings?.isFacilityCategoryViewVisible || false;

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.FACILITY_LIST,
  });

  const facilitiesVisibleToMembers =
    property?.modulesExcludingDisabled?.spa?.features?.facilities?.features?.visibleToMembers
      ?.enabled;

  const availableFacilities = facilities
    .sort((a, b) => a.priority - b.priority)
    .filter((item) => hasAvailability(item, journeyParams.fromDate));

  const isLoading = isFetchingFacilities;

  return (
    <>
      {userHasAccessToFacilities ? (
        <>
          <Spinner isActive={isLoading} showDelayMessage />
          {isFacilityCategoryViewVisible ? (
            <SpaEntityCategoryView
              property={property}
              categories={facilitiesCategories}
              spaEntityPage={facilityPage}
              journeyParams={journeyParams}
              changeJourneyParam={changeJourneyParam}
              categoryRoute={Routes.FACILITY_CATEGORY_DETAIL}
              defaultTitle="app.facilities"
            />
          ) : (
            <EntitiesListing
              entityRoute={Routes.FACILITY_DETAIL}
              entities={availableFacilities.map((t) => ({ ...t, imageGallery: [t.image] }))}
              entitiesAll={facilities}
              entitiesPage={facilityPage}
              journeyParams={journeyParams}
              entitiesCategories={facilitiesCategories}
              defaultTitle="app.facilities"
              listingRoute={Routes.FACILITY_LIST}
              changeJourneyParam={changeJourneyParam}
              setCategoryFilterId={setCategoryFilterId}
              categoryFilterId={categoryFilterId}
            />
          )}
        </>
      ) : (
        <SpaEntityError
          errorMessage={facilitiesVisibleToMembers ? 'app.mustBeMember' : 'app.mustBeLoggedIn'}
          values={{
            spaEntity: 'facilities',
          }}
        />
      )}
    </>
  );
};
